// The notification rule registry — the single source of truth for "what can
// Ordo send someone, on which channels, and is it on by default".
//
// Historically this vocabulary was hand-mirrored in FOUR places:
// `packages/schema/src/managers/notifications-defaults.ts` (the runtime default
// matrix), `apps/ordo/client/src/api/notifications.ts` (twice — a defaults copy
// and a labels/sections copy), and
// `apps/customers/server/src/managers/notifications.ts` (the staff subset).
// Nothing pinned the VALUES: the ordo contract test checks category NAMES
// against the Prisma enum, so a default could flip from true to false in the
// schema and the client would keep rendering the toggle as on. PROD-383 already
// shipped a production 500 from that drift class.
//
// This module is the fix. `@ordo/constants` has no runtime dependencies, so the
// customers admin UI, the ordo client, the ordo server, and `@ordo/schema`
// itself can all import the same rows. `notifications-defaults.ts` re-keys this
// registry by the Prisma `NotificationCategory` enum, which turns any future
// divergence into a COMPILE error rather than a silent one.
//
// Two kinds of rule live here:
//
//   - Rule keys that ARE a `NotificationCategory` (`category` set). These flow
//     through `NotificationsManager.notify()`, which writes a Notification row
//     plus one NotificationDelivery per resolved channel.
//   - Rule keys that are NOT (`category` undefined, dotted keys like
//     `access.magic_link`). These are direct `@ordo/boombox-client` sends with
//     no Notification row. They are governed by calling
//     `isNotificationRuleEnabled` at the send site.
//
// ADDING A NOTIFICATION? Add its rule here in the same PR. That is what makes
// it appear in Customers → Settings → Notifications and what makes it
// switchable. A notification with no registry entry is ungoverned by
// construction. See the "Notification coverage policy" in the root AGENTS.md.

/**
 * Delivery channels. Mirrors the Prisma `NotificationChannel` enum as plain
 * strings so this module stays browser-safe and Prisma-free.
 */
export const NOTIFICATION_RULE_CHANNELS = ['IN_APP', 'EMAIL', 'SMS', 'PUSH'] as const

export type NotificationRuleChannel = (typeof NOTIFICATION_RULE_CHANNELS)[number]

/** Per-channel on/off, used for both shipped defaults and resolved state. */
export type NotificationChannelMatrix = Record<NotificationRuleChannel, boolean>

/**
 * Operator-facing grouping on the Customers settings page. Order here is the
 * order the sections render in.
 */
export const NOTIFICATION_RULE_SECTIONS = [
  'orders',
  'billing',
  'family',
  'operations',
  'messages',
  'access',
  'internal',
  'other',
] as const

export type NotificationRuleSection = (typeof NOTIFICATION_RULE_SECTIONS)[number]

export const NOTIFICATION_RULE_SECTION_LABELS: Record<NotificationRuleSection, string> = {
  orders: 'Orders',
  billing: 'Billing and wallet',
  family: 'Family',
  operations: 'Kitchen and school operations',
  messages: 'Messages',
  access: 'Access and credentials',
  internal: 'Internal (Ordo team)',
  other: 'Other',
}

/**
 * How close a rule is to actually reaching someone. This is deliberately
 * honest: an operator looking at this page needs to tell "switched off" apart
 * from "we never built the sender", and apart from "the code is there but ops
 * has not configured its Boombox template".
 */
export const NOTIFICATION_RULE_STATUSES = ['LIVE', 'NEEDS_CONFIG', 'NOT_BUILT'] as const

export type NotificationRuleStatus = (typeof NOTIFICATION_RULE_STATUSES)[number]

export const NOTIFICATION_RULE_STATUS_LABELS: Record<NotificationRuleStatus, string> = {
  LIVE: 'Live',
  NEEDS_CONFIG: 'Needs config',
  NOT_BUILT: 'Not built',
}

/**
 * Who on a household receives a rule's notification.
 *
 * PAYER is today's behaviour everywhere: every producer targets
 * `Account.ownerUserId`. ALL_GUARDIANS fans out over `HouseholdAccess`, which
 * no producer has ever queried — a secondary guardian currently receives zero
 * billing mail.
 */
export const NOTIFICATION_RECIPIENT_SCOPES = ['PAYER', 'ALL_GUARDIANS'] as const

export type NotificationRecipientScope = (typeof NOTIFICATION_RECIPIENT_SCOPES)[number]

export const NOTIFICATION_RECIPIENT_SCOPE_LABELS: Record<NotificationRecipientScope, string> = {
  PAYER: 'Payer only',
  ALL_GUARDIANS: 'Every guardian on the household',
}

/** How a trigger parameter is entered and rendered. */
export type NotificationRuleParamKind = 'MONEY_CENTS' | 'HOURS'

/**
 * A tunable input to a rule's TRIGGER — the "when", not the "whether".
 *
 * Most rules have none: the 13 live ones are event-driven (a Stripe webhook
 * lands, a checkout completes, an invite is created), so there is nothing to
 * tune. Only the threshold- and schedule-driven rules carry params, and a
 * param is only ever declared here when a producer actually reads it.
 */
export interface NotificationRuleParamDef {
  key: string
  label: string
  help: string
  kind: NotificationRuleParamKind
  /** Shipped value, used when no operator override exists. */
  default: number
  min: number
  max: number
}

export interface NotificationRuleDefinition {
  /**
   * Stable identifier. For rules that ride the notification rail this IS the
   * Prisma `NotificationCategory` value, so category to rule is identity. For
   * direct Boombox sends it is a dotted key that will never collide with an
   * enum member.
   */
  key: string
  /**
   * The `NotificationCategory` this rule governs, or `undefined` for a direct
   * Boombox send that never creates a Notification row. Required rather than
   * optional so every rule states which rail it is on instead of leaving it to
   * an omission.
   */
  category: string | undefined
  section: NotificationRuleSection
  /** Short name shown in the settings row. */
  label: string
  /** What the recipient gets. */
  description: string
  /** What causes it to fire. Operator-facing, one line. */
  trigger: string
  /** Who receives it. */
  audience: string
  /** Channels this rule can ever use. Others are hidden in the row. */
  channels: readonly NotificationRuleChannel[]
  /** Shipped per-channel default, used when no override row exists. */
  defaults: NotificationChannelMatrix
  status: NotificationRuleStatus
  /**
   * False for sends an operator must never be able to switch off. Today that
   * is credential and account-access mail only: invitations, magic links,
   * password resets, signup codes, and email-change verification. Switching
   * any of them off locks people out of the product rather than expressing a
   * preference. These render as locked rows.
   */
  governable: boolean
  /**
   * Tunable trigger inputs. EMPTY for event-driven rules, which is most of
   * them — an empty list is what tells the UI there is no "Edit trigger"
   * affordance to offer.
   */
  params: readonly NotificationRuleParamDef[]
  /**
   * Recipient scopes an operator may choose between. Empty for rules with no
   * household (internal mail, credential mail), which pin their recipient by
   * construction. First entry is the default.
   */
  recipientScopes: readonly NotificationRecipientScope[]
}

/** Every channel off. Helper so the direct-send rows stay readable. */
const EMAIL_ONLY: NotificationChannelMatrix = { IN_APP: false, EMAIL: true, SMS: false, PUSH: false }

export const NOTIFICATION_RULES = [
  // ---------------------------------------------------------------------------
  // Orders
  // ---------------------------------------------------------------------------
  {
    key: 'ORDER_CONFIRMED',
    category: 'ORDER_CONFIRMED',
    section: 'orders',
    label: 'Order confirmed',
    description: 'One summary of everything a parent just ordered, sent after checkout settles.',
    trigger: 'Parent checkout succeeds. Sends are aggregated over a 30 second quiet window.',
    audience: 'The person who placed the order.',
    channels: ['IN_APP', 'EMAIL', 'SMS', 'PUSH'],
    defaults: { IN_APP: true, EMAIL: true, SMS: false, PUSH: false },
    // The sender exists (OrderConfirmationEmailer) but ships with a placeholder
    // template slug and ORDER_CONFIRMATION_EMAIL_TEMPLATE_SLUG is set in no
    // chart, so nothing is delivered in production today.
    status: 'NEEDS_CONFIG',
    governable: true,
    params: [],
    recipientScopes: ['PAYER', 'ALL_GUARDIANS'],
  },
  {
    key: 'ORDER_FAILED',
    category: 'ORDER_FAILED',
    section: 'orders',
    label: 'Order failed',
    description:
      'When an order could not be placed or was rejected — today, a payment-pending order the automatic retry had to cancel (ordering closed first, or the wallet was still short).',
    trigger:
      'The hourly payment-retries pass voids a payment-pending order (PROD-1299). Sent as in-app + email; the email drains through the generic notification template.',
    audience: 'The household payer (the primary guardian).',
    channels: ['IN_APP', 'EMAIL', 'SMS', 'PUSH'],
    defaults: { IN_APP: true, EMAIL: true, SMS: true, PUSH: false },
    // The sender (`FamilyOrderManager.notifyOrderVoided`) exists and the
    // EMAIL row is delivered by the same generic template every rail
    // notification uses — LIVE to the same degree PAYMENT_FAILED is.
    status: 'LIVE',
    governable: true,
    params: [],
    recipientScopes: ['PAYER', 'ALL_GUARDIANS'],
  },
  {
    key: 'ORDER_REMINDER',
    category: 'ORDER_REMINDER',
    section: 'orders',
    label: 'Order reminder',
    description: 'Reminders about upcoming meals a family has already ordered.',
    trigger: 'The nightly pass finds placed orders whose service date falls inside the lead time below.',
    audience: 'Household payer.',
    channels: ['IN_APP', 'EMAIL', 'SMS', 'PUSH'],
    // Ships OFF on every channel — see ACCOUNT_LOW_BALANCE.
    defaults: { IN_APP: false, EMAIL: false, SMS: false, PUSH: false },
    status: 'LIVE',
    governable: true,
    params: [
      {
        key: 'leadHours',
        label: 'Send ahead of service',
        help: 'How far before the meal is served the reminder goes out. Rounded to the nightly pass.',
        kind: 'HOURS',
        default: 24,
        min: 1,
        max: 168,
      },
    ],
    recipientScopes: ['PAYER', 'ALL_GUARDIANS'],
  },
  {
    key: 'ORDER_ITEM_REPLACED',
    category: 'ORDER_ITEM_REPLACED',
    section: 'orders',
    label: 'Dish changed',
    description:
      "When a school changes kitchens, or its menu is replaced by a new release, and meals a family already ordered move to a different dish: one message per change, listing each meal's old and new dish.",
    trigger:
      "A chef handover is applied, or a released menu's dish decisions move stranded orders, and some of the household's order lines change dish (PROD-1521). Sent as in-app + email through the generic notification template.",
    audience: 'The household payer.',
    // No SMS or push dispatcher exists on the notification rail.
    channels: ['IN_APP', 'EMAIL'],
    // Ships OFF on every channel — test-only first (Adam, 2026-10-19). The rail
    // writes IN_APP rows whatever the default, so the sender checks EMAIL here
    // before writing anything: switching EMAIL on is what turns it on.
    defaults: { IN_APP: false, EMAIL: false, SMS: false, PUSH: false },
    status: 'LIVE',
    governable: true,
    params: [],
    recipientScopes: ['PAYER'],
  },
  {
    key: 'ORDER_CUTOFF_SOON',
    category: 'ORDER_CUTOFF_SOON',
    section: 'orders',
    label: 'Order cutoff soon',
    description: 'Last call before an ordering window closes.',
    trigger: 'The hourly sweep finds an ordering window closing inside the lead time below, with nothing ordered yet.',
    audience: 'Household payer.',
    channels: ['IN_APP', 'EMAIL', 'SMS', 'PUSH'],
    // Ships OFF on every channel — see ACCOUNT_LOW_BALANCE.
    defaults: { IN_APP: false, EMAIL: false, SMS: false, PUSH: false },
    status: 'LIVE',
    governable: true,
    params: [
      {
        key: 'leadHours',
        label: 'Send before cutoff',
        help: 'How long before the ordering window closes the last-call notice goes out.',
        kind: 'HOURS',
        default: 2,
        min: 1,
        max: 72,
      },
    ],
    recipientScopes: ['PAYER', 'ALL_GUARDIANS'],
  },
  {
    key: 'ORDER_RECEIPT',
    category: 'ORDER_RECEIPT',
    section: 'orders',
    label: 'Order receipt',
    description: 'The itemized receipt after a meal is served.',
    trigger: 'No sender built yet.',
    audience: 'Household payer.',
    channels: ['IN_APP', 'EMAIL'],
    defaults: { IN_APP: false, EMAIL: true, SMS: false, PUSH: false },
    status: 'NOT_BUILT',
    governable: true,
    params: [],
    recipientScopes: ['PAYER', 'ALL_GUARDIANS'],
  },

  // ---------------------------------------------------------------------------
  // Billing and wallet
  // ---------------------------------------------------------------------------
  {
    key: 'PAYMENT_FAILED',
    category: 'PAYMENT_FAILED',
    section: 'billing',
    label: 'Payment failed',
    description:
      'A charge on the household wallet did not go through. Covers a declined auto-reload, an ACH transfer that did not clear, and a removed default card that leaves auto-reload with nothing to charge. While the failure stays unfixed, reminders follow 3 and 7 days after the first email, and one last email says when new ordering pauses on day 13.',
    trigger:
      'Stripe reports a failed payment intent or declines an auto-reload charge, or the default card is detached. Reminders come from the hourly notification sweep.',
    audience: 'The household payer (the primary guardian).',
    channels: ['IN_APP', 'EMAIL', 'SMS'],
    defaults: { IN_APP: true, EMAIL: true, SMS: true, PUSH: false },
    status: 'LIVE',
    governable: true,
    params: [],
    recipientScopes: ['PAYER', 'ALL_GUARDIANS'],
  },
  {
    key: 'PAYMENT_RECEIVED',
    category: 'PAYMENT_RECEIVED',
    section: 'billing',
    label: 'Payment received',
    description: 'When a payment is applied to the household wallet.',
    trigger: 'No sender built yet.',
    audience: 'The household payer.',
    channels: ['IN_APP', 'EMAIL'],
    defaults: { IN_APP: false, EMAIL: true, SMS: false, PUSH: false },
    status: 'NOT_BUILT',
    governable: true,
    params: [],
    recipientScopes: ['PAYER', 'ALL_GUARDIANS'],
  },
  {
    // A DIRECT send: Stripe owns the invoice email and takes exactly one
    // `customer.email`, so this rail exists only to reach the additional
    // people an org lists in `Organization.billingCcEmails`. It writes no
    // Notification row, so `isNotificationRuleEnabled` at the send site in
    // `apps/subscription-runner/src/jobs/school-invoice-copies.ts` is the
    // ONLY thing governing it.
    key: 'billing.invoice_copy',
    category: undefined,
    section: 'billing',
    label: 'School invoice copy',
    description:
      'A copy of a school invoice, with the hosted payment link and the summary PDF, for the additional people the organization listed as invoice recipients. The primary billing contact is emailed by Stripe and is never affected by this rule.',
    trigger: 'An invoice was successfully sent to Stripe and the organization lists additional invoice recipients.',
    audience: "The addresses in the organization's invoice-copy list.",
    channels: ['EMAIL'],
    // ON by default: an operator adding someone to the copy list has already
    // asked for this mail, and a list that silently sends nothing is worse
    // than no list at all. Switchable off per-org-wide in Settings.
    defaults: EMAIL_ONLY,
    // The sender exists and the Boombox template slug
    // (`school-invoice-copy-1`) is created and live.
    status: 'LIVE',
    governable: true,
    params: [],
    recipientScopes: [],
  },
  {
    key: 'ACCOUNT_LOW_BALANCE',
    category: 'ACCOUNT_LOW_BALANCE',
    section: 'billing',
    label: 'Low or negative balance',
    description: 'When a household wallet runs low or goes negative and ordering is at risk.',
    trigger:
      'No sender built yet. The register writes a low balance notice to the log only, and the payment failure banner is derived in the app rather than sent.',
    audience: 'The household payer.',
    channels: ['IN_APP', 'EMAIL', 'SMS'],
    // Ships OFF on every channel: the sender is real, but nobody asked for a
    // new class of mail to start landing in parents' inboxes the moment this
    // deploys. An operator turns it on in Customers → Settings → Notifications.
    defaults: { IN_APP: false, EMAIL: false, SMS: false, PUSH: false },
    status: 'LIVE',
    governable: true,
    params: [
      {
        key: 'thresholdCents',
        label: 'Warn below',
        help: 'Send once a household wallet drops under this amount. A negative balance always qualifies.',
        kind: 'MONEY_CENTS',
        default: 500,
        min: 0,
        max: 100_000,
      },
    ],
    recipientScopes: ['PAYER', 'ALL_GUARDIANS'],
  },
  {
    key: 'BALANCE_CLAWBACK',
    category: 'BALANCE_CLAWBACK',
    section: 'billing',
    label: 'Refund or dispute withdrawal',
    description:
      'Money left the wallet because Stripe pulled it back. Covers a card refund, a bank inquiry, a dispute placing funds on hold, and a dispute resolved in our favor. A real dispute ALSO disables ordering and PAUSES the household auto-orders (they are restored when the block is lifted), and this rule governs the mail that says so — switching EMAIL off here means a frozen family is never told why.',
    trigger: 'A Stripe refund or dispute event lands on a household wallet.',
    audience: 'The household payer.',
    channels: ['IN_APP', 'EMAIL'],
    defaults: { IN_APP: true, EMAIL: true, SMS: false, PUSH: false },
    status: 'LIVE',
    governable: true,
    params: [],
    recipientScopes: ['PAYER', 'ALL_GUARDIANS'],
  },

  // ---------------------------------------------------------------------------
  // Family
  // ---------------------------------------------------------------------------
  {
    key: 'CHILD_SERVED_MEAL',
    category: 'CHILD_SERVED_MEAL',
    section: 'family',
    label: 'Child served a meal',
    description: 'When a child on the account is served at the cafeteria.',
    trigger: 'No sender built yet.',
    audience: 'Guardians on the household.',
    channels: ['IN_APP', 'EMAIL', 'PUSH'],
    defaults: { IN_APP: true, EMAIL: false, SMS: false, PUSH: true },
    status: 'NOT_BUILT',
    governable: true,
    params: [],
    recipientScopes: ['PAYER', 'ALL_GUARDIANS'],
  },
  {
    key: 'CHILD_UNSERVED_FLAGGED',
    category: 'CHILD_UNSERVED_FLAGGED',
    section: 'family',
    label: 'Missed meal',
    description: 'When a child has an ordered meal flagged as not served.',
    trigger: 'No sender built yet.',
    audience: 'Guardians on the household.',
    channels: ['IN_APP', 'EMAIL', 'SMS', 'PUSH'],
    defaults: { IN_APP: true, EMAIL: true, SMS: true, PUSH: true },
    status: 'NOT_BUILT',
    governable: true,
    params: [],
    recipientScopes: ['PAYER', 'ALL_GUARDIANS'],
  },

  // ---------------------------------------------------------------------------
  // Kitchen and school operations
  // ---------------------------------------------------------------------------
  {
    key: 'SHIPMENT_STATUS_CHANGE',
    category: 'SHIPMENT_STATUS_CHANGE',
    section: 'operations',
    label: 'Shipment status change',
    description: 'Updates as a shipment moves through fulfillment.',
    trigger: 'No sender built yet.',
    audience: 'Kitchen and school staff.',
    channels: ['IN_APP', 'EMAIL'],
    defaults: { IN_APP: true, EMAIL: false, SMS: false, PUSH: false },
    status: 'NOT_BUILT',
    governable: true,
    params: [],
    recipientScopes: [],
  },
  {
    key: 'PRODUCTION_RECORD_DUE',
    category: 'PRODUCTION_RECORD_DUE',
    section: 'operations',
    label: 'Production record due',
    description: 'When a USDA production record needs to be filed.',
    trigger: 'No sender built yet.',
    audience: 'Kitchen staff.',
    channels: ['IN_APP', 'EMAIL'],
    defaults: { IN_APP: true, EMAIL: true, SMS: false, PUSH: false },
    status: 'NOT_BUILT',
    governable: true,
    params: [],
    recipientScopes: [],
  },
  {
    // PROD-1503. A DIRECT send (`category: undefined`): the hourly
    // notification sweep (`apps/ordo/server/src/managers/chef-orders-ready.ts`)
    // calls `@ordo/boombox-client` per recipient and channel, gated by
    // `isNotificationRuleEnabled`. SMS is safe here for the same reason as
    // `invitations.sms`: no Notification row, so nothing strands PENDING.
    key: 'chef.orders_ready',
    category: undefined,
    section: 'operations',
    label: 'Chef: orders ready to print',
    description:
      "Emails and texts the kitchen's chef admins a link that opens the week's print packet, once every school's order window for that week has closed and the counts are final.",
    trigger:
      "The last school order deadline for the kitchen's next delivery week passes (the same lock the print dialog uses). Once per kitchen, week and channel.",
    audience:
      'Chef admins of the kitchen (CHEF_ADMIN membership at the kitchen or org-wide): email to those with an email, text to those with a mobile number.',
    channels: ['EMAIL', 'SMS'],
    // OFF on both channels, deliberately (Abner, 2026-10-14: "don't put live
    // because I'll want to test first"). Texting chefs also waits on A2P
    // campaign coverage and the consent model (Hunter).
    defaults: { IN_APP: false, EMAIL: false, SMS: false, PUSH: false },
    // Sender built and wired; both Boombox templates (`chef-orders-ready-email`,
    // `chef-orders-ready-sms`) exist in prod Boombox and are the default slugs.
    status: 'LIVE',
    governable: true,
    params: [],
    recipientScopes: [],
  },

  // ---------------------------------------------------------------------------
  // Messages
  // ---------------------------------------------------------------------------
  {
    key: 'NEW_MESSAGE',
    category: 'NEW_MESSAGE',
    section: 'messages',
    label: 'New message',
    description: 'When someone receives a new direct message or chat reply.',
    trigger: 'No sender built yet. The message inbox never fires this.',
    audience: 'The thread participant.',
    channels: ['IN_APP', 'EMAIL', 'PUSH'],
    defaults: { IN_APP: true, EMAIL: false, SMS: false, PUSH: true },
    status: 'NOT_BUILT',
    governable: true,
    params: [],
    recipientScopes: [],
  },

  // ---------------------------------------------------------------------------
  // Access and credentials
  // ---------------------------------------------------------------------------
  {
    key: 'ACCOUNT_INVITATION',
    category: 'ACCOUNT_INVITATION',
    section: 'access',
    label: 'Invitation to join Ordo',
    description:
      'The invite that lets someone claim an Ordo account. Covers co-guardian invites, roster imports, approved access requests, and operator invites.',
    trigger: 'An invitation is created in Ordo or Customers, or the nightly dispatcher drains a queued one.',
    audience: 'The invited email address.',
    channels: ['IN_APP', 'EMAIL'],
    defaults: { IN_APP: false, EMAIL: true, SMS: false, PUSH: false },
    status: 'LIVE',
    // Access mail, same class as a magic link: the invitation IS how someone
    // gets into the account. Six send sites across three apps would go quiet
    // while every UI kept reporting "invite sent", so a switch here buys a
    // silent onboarding outage in exchange for an ops control nobody has asked
    // for. Flip to true and gate the six sites if that trade ever changes.
    governable: false,
    params: [],
    recipientScopes: [],
  },
  {
    // The SMS half of the invitation above (FACTORY-451). A SEPARATE rule, not
    // an `SMS` entry on ACCOUNT_INVITATION, for two reasons that both cut the
    // same way:
    //
    //   - ACCOUNT_INVITATION is `governable: false`, so an override row on it
    //     is ignored by construction (`isNotificationRuleEnabled` short-circuits
    //     to true). The text leg is exactly the thing that DOES need a switch:
    //     it is new, it costs money per message, and carrier/consent trouble
    //     has to be stoppable in seconds without a deploy. The email leg stays
    //     locked, so switching this off can never lock anyone out of Ordo —
    //     which is the whole reason ACCOUNT_INVITATION is ungovernable.
    //   - ACCOUNT_INVITATION rides the notification rail, and there is no SMS
    //     dispatcher on that rail (`notification-email-dispatcher.ts` queries
    //     `channel: 'EMAIL'` only), so an SMS row there would strand PENDING
    //     forever. This rule is a DIRECT send (`category: undefined`): the six
    //     invite send sites call `sendInvitationSmsLeg`, which goes straight to
    //     `@ordo/boombox-client`. No Notification row, nothing to strand.
    key: 'invitations.sms',
    category: undefined,
    section: 'access',
    label: 'Invitation text message',
    description:
      'A text carrying the same account-creation link as the invitation email, sent alongside it so a spam-foldered invite is not a dead end.',
    trigger: 'An invitation is sent, when the invitee has a usable mobile number on file and has not replied STOP.',
    audience: 'The invited person, on the phone number on their Ordo user record.',
    channels: ['SMS'],
    // OFF everywhere, deliberately: this ships inert. Turning it on is gated on
    // the consent model being signed off (FACTORY-455) and A2P campaign
    // coverage being confirmed for transactional invite traffic
    // (FACTORY-457) — see docs/launch/invite-sms-enablement.md.
    defaults: { IN_APP: false, EMAIL: false, SMS: false, PUSH: false },
    // Sender is built and wired, but the `invitation-sms` template body is
    // authored per-environment in the Boombox admin UI (only a dev stand-in
    // ships in the repo), so this is not honestly LIVE until ops writes it.
    status: 'NEEDS_CONFIG',
    governable: true,
    params: [],
    recipientScopes: [],
  },
  {
    key: 'access.magic_link',
    category: undefined,
    section: 'access',
    label: 'Magic link sign-in',
    description: 'The one-time sign-in link a user requests when they cannot or will not use a password.',
    trigger: 'A user requests a magic link on the sign-in screen.',
    audience: 'The address entered on the sign-in form.',
    channels: ['EMAIL'],
    defaults: EMAIL_ONLY,
    status: 'LIVE',
    governable: false,
    params: [],
    recipientScopes: [],
  },
  {
    key: 'access.password_reset',
    category: undefined,
    section: 'access',
    label: 'Password reset',
    description: 'The link that lets a user set a new password.',
    trigger: 'A user requests a reset, or an operator triggers one from Customers.',
    audience: 'The account holder.',
    channels: ['EMAIL'],
    defaults: EMAIL_ONLY,
    status: 'LIVE',
    governable: false,
    params: [],
    recipientScopes: [],
  },
  {
    key: 'access.signup_verification',
    category: undefined,
    section: 'access',
    label: 'Signup verification code',
    description: 'The one-time code that proves a new signup owns the address they entered.',
    trigger: 'A new user starts signup.',
    audience: 'The signup email address.',
    channels: ['EMAIL'],
    defaults: EMAIL_ONLY,
    status: 'LIVE',
    governable: false,
    params: [],
    recipientScopes: [],
  },
  {
    key: 'access.email_change_verify',
    category: undefined,
    section: 'access',
    label: 'Confirm a new email address',
    description: 'The link that confirms a user owns the address they are switching to.',
    trigger: 'A user changes the email on their profile.',
    audience: 'The new address, not the old one.',
    channels: ['EMAIL'],
    defaults: EMAIL_ONLY,
    status: 'LIVE',
    governable: false,
    params: [],
    recipientScopes: [],
  },

  // ---------------------------------------------------------------------------
  // Internal (Ordo team)
  // ---------------------------------------------------------------------------
  {
    key: 'TASK_ASSIGNED',
    category: 'TASK_ASSIGNED',
    section: 'internal',
    label: 'Task assigned',
    description: 'When a task is assigned to you on the Tasks page.',
    trigger: 'No sender built yet. The overdue escalation helper exists but nothing calls it.',
    audience: 'The assignee.',
    channels: ['IN_APP', 'EMAIL'],
    defaults: { IN_APP: true, EMAIL: false, SMS: false, PUSH: false },
    status: 'NOT_BUILT',
    governable: true,
    params: [],
    recipientScopes: [],
  },
  {
    key: 'DEAL_STAGE_CHANGE',
    category: 'DEAL_STAGE_CHANGE',
    section: 'internal',
    label: 'Deal stage change',
    description: 'When a sales deal you own moves to a new stage.',
    trigger: 'No sender built yet.',
    audience: 'The deal owner.',
    channels: ['IN_APP', 'EMAIL'],
    defaults: { IN_APP: true, EMAIL: false, SMS: false, PUSH: false },
    status: 'NOT_BUILT',
    governable: true,
    params: [],
    recipientScopes: [],
  },
  {
    key: 'RFP_BID_DUE',
    category: 'RFP_BID_DUE',
    section: 'internal',
    label: 'RFP bid due',
    description: 'Approaching deadlines for RFP bids.',
    trigger: 'No sender built yet on this rail.',
    audience: 'The bid owner.',
    channels: ['IN_APP', 'EMAIL'],
    defaults: { IN_APP: true, EMAIL: true, SMS: false, PUSH: false },
    status: 'NOT_BUILT',
    governable: true,
    params: [],
    recipientScopes: [],
  },
  {
    key: 'CUSTOMER_NOTE_MENTION',
    category: 'CUSTOMER_NOTE_MENTION',
    section: 'internal',
    label: 'Mentioned in a customer note',
    description: 'When a teammate @mentions you on a customer record note.',
    trigger: 'A note containing your mention is posted in Customers.',
    audience: 'The mentioned teammate.',
    channels: ['IN_APP', 'EMAIL', 'PUSH'],
    defaults: { IN_APP: true, EMAIL: true, SMS: false, PUSH: true },
    status: 'LIVE',
    governable: true,
    params: [],
    recipientScopes: [],
  },
  {
    key: 'CUSTOMER_NOTE_REPLY',
    category: 'CUSTOMER_NOTE_REPLY',
    section: 'internal',
    label: 'Reply to a customer note',
    description: 'When someone replies to a customer record note you are on.',
    trigger: 'A reply is posted in Customers.',
    audience: 'Participants on the note.',
    channels: ['IN_APP', 'EMAIL', 'PUSH'],
    defaults: { IN_APP: true, EMAIL: true, SMS: false, PUSH: true },
    status: 'LIVE',
    governable: true,
    params: [],
    recipientScopes: [],
  },
  {
    key: 'CUSTOMER_TASK_ASSIGNED',
    category: 'CUSTOMER_TASK_ASSIGNED',
    section: 'internal',
    label: 'Customer task assigned',
    description: 'When a customer task is assigned to you.',
    trigger: 'A task is assigned in Customers.',
    audience: 'The assignee.',
    channels: ['IN_APP', 'EMAIL', 'PUSH'],
    defaults: { IN_APP: true, EMAIL: true, SMS: false, PUSH: true },
    status: 'LIVE',
    governable: true,
    params: [],
    recipientScopes: [],
  },
  {
    key: 'CUSTOMER_WORK_ORDER_ASSIGNED',
    category: 'CUSTOMER_WORK_ORDER_ASSIGNED',
    section: 'internal',
    label: 'Work order assigned',
    description: 'When a work order is assigned to you.',
    trigger: 'A work order is assigned in Customers.',
    audience: 'The assignee.',
    channels: ['IN_APP', 'EMAIL', 'PUSH'],
    defaults: { IN_APP: true, EMAIL: true, SMS: false, PUSH: true },
    status: 'LIVE',
    governable: true,
    params: [],
    recipientScopes: [],
  },
  {
    key: 'school_onboarding.notification',
    category: undefined,
    section: 'internal',
    label: 'School onboarding update',
    description: 'Progress and completion mail sent to a school during onboarding.',
    trigger: 'An onboarding step is notified or the checklist completes.',
    audience: 'School admins at the onboarding school.',
    channels: ['EMAIL'],
    defaults: EMAIL_ONLY,
    status: 'LIVE',
    governable: true,
    params: [],
    recipientScopes: [],
  },

  // ---------------------------------------------------------------------------
  // Other
  // ---------------------------------------------------------------------------
  {
    key: 'SYSTEM_ANNOUNCEMENT',
    category: 'SYSTEM_ANNOUNCEMENT',
    section: 'other',
    label: 'System announcement',
    description:
      'Platform-wide notices from Ordo. Today the only sender is the subscription price change notice, which tells a payer their recurring meal price moved.',
    trigger: 'The daily auto-order pass detects a unit price change, or an operator posts an announcement.',
    audience: 'The affected payer.',
    channels: ['IN_APP', 'EMAIL'],
    defaults: { IN_APP: true, EMAIL: false, SMS: false, PUSH: false },
    status: 'LIVE',
    governable: true,
    params: [],
    recipientScopes: [],
  },
  {
    key: 'OTHER',
    category: 'OTHER',
    section: 'other',
    label: 'Uncategorized',
    description: 'Catch-all bucket for notifications that do not fit another rule.',
    trigger: 'No sender built yet.',
    audience: 'Varies.',
    channels: ['IN_APP', 'EMAIL'],
    defaults: { IN_APP: true, EMAIL: false, SMS: false, PUSH: false },
    status: 'NOT_BUILT',
    governable: true,
    params: [],
    recipientScopes: [],
  },
] as const satisfies readonly NotificationRuleDefinition[]

/**
 * Literal union of every rule key. `as const satisfies` above is what makes
 * this derivable — consumers get real autocomplete and typo protection without
 * anyone maintaining a second list.
 */
export type NotificationRuleKey = (typeof NOTIFICATION_RULES)[number]['key']

/** Literal union of the `NotificationCategory` values the registry governs. */
export type NotificationRuleCategoryKey = NonNullable<(typeof NOTIFICATION_RULES)[number]['category']>

/** Registry indexed by rule key. */
export const NOTIFICATION_RULES_BY_KEY: Readonly<Record<string, NotificationRuleDefinition>> = Object.freeze(
  Object.fromEntries(NOTIFICATION_RULES.map((rule) => [rule.key, rule])),
)

/** Registry indexed by the `NotificationCategory` a rule governs, when it has one. */
export const NOTIFICATION_RULES_BY_CATEGORY: Readonly<Record<string, NotificationRuleDefinition>> = Object.freeze(
  Object.fromEntries(
    NOTIFICATION_RULES.filter((rule) => rule.category !== undefined).map((rule) => [rule.category as string, rule]),
  ),
)

/** True when `key` names a rule in the registry. Guards the write path. */
export function isNotificationRuleKey(key: string): boolean {
  return Object.hasOwn(NOTIFICATION_RULES_BY_KEY, key)
}

/**
 * True when `key` names a rule an operator is allowed to switch. Unknown keys
 * are NOT governable — fail closed so a typo can never disable real mail.
 */
export function isGovernableNotificationRuleKey(key: string): boolean {
  return NOTIFICATION_RULES_BY_KEY[key]?.governable === true
}

/** The rule that governs a `NotificationCategory`, if one is registered. */
export function notificationRuleForCategory(category: string): NotificationRuleDefinition | undefined {
  return NOTIFICATION_RULES_BY_CATEGORY[category]
}

/** Rules that expose a tunable trigger. Drives the "Edit trigger" affordance. */
export function notificationRuleHasParams(key: string): boolean {
  return (NOTIFICATION_RULES_BY_KEY[key]?.params.length ?? 0) > 0
}

/** A rule's param definition, or undefined when the rule does not declare it. */
export function notificationRuleParam(key: string, paramKey: string): NotificationRuleParamDef | undefined {
  return NOTIFICATION_RULES_BY_KEY[key]?.params.find((p) => p.key === paramKey)
}

/**
 * Clamp + fill a stored param bag against the rule's declarations. Unknown keys
 * are DROPPED and out-of-range values are clamped rather than rejected: a stale
 * blob left behind by a renamed param must never stop a notification resolving.
 */
export function resolveNotificationRuleParams(
  key: string,
  stored: Record<string, unknown> | null | undefined,
): Record<string, number> {
  const rule = NOTIFICATION_RULES_BY_KEY[key]
  if (!rule) {
    return {}
  }
  const out: Record<string, number> = {}
  for (const def of rule.params) {
    const raw = stored?.[def.key]
    const value = typeof raw === 'number' && Number.isFinite(raw) ? raw : def.default
    out[def.key] = Math.min(def.max, Math.max(def.min, Math.round(value)))
  }
  return out
}

/** The default recipient scope for a rule (its first declared scope). */
export function defaultNotificationRecipientScope(key: string): NotificationRecipientScope | undefined {
  return NOTIFICATION_RULES_BY_KEY[key]?.recipientScopes[0]
}

/** Rules in a section, in registry order. */
export function notificationRulesInSection(section: NotificationRuleSection): NotificationRuleDefinition[] {
  return NOTIFICATION_RULES.filter((rule) => rule.section === section)
}

// ---------------------------------------------------------------------------
// Unified inbox (internal apps)
// ---------------------------------------------------------------------------
//
// The staff-facing notifications that Tasks, RFPs, Customers and Factory send
// today, as they will be stored by the central notifications service. These are
// a separate list from NOTIFICATION_RULES on purpose: the rules above govern the
// customer-facing rail (and render on Customers → Settings → Notifications),
// while these are governed per person from HQ, with admin-set defaults. Keeping
// them apart means adding the inbox can never change what the Customers page or
// the ordo client renders.
//
// Version 1 carries ONLY types that exist in an app today. Every row starts
// NOT_BUILT: the app sends it into its own table, but nothing sends it to the
// unified inbox yet. Each app's dual-write PR flips its rows to LIVE.

/** Apps that send into the unified inbox. */
export const INBOX_APPS = ['tasks', 'rfps', 'customers', 'factory', 'schedule', 'hq'] as const

export type InboxApp = (typeof INBOX_APPS)[number]

export const INBOX_APP_LABELS: Record<InboxApp, string> = {
  tasks: 'Tasks',
  rfps: 'RFPs',
  customers: 'Customers',
  factory: 'Factory',
  schedule: 'Schedule',
  hq: 'HQ',
}

/**
 * The tab a notification lands in for ONE recipient. The sender picks it per
 * person (the same event can be Assigned for one teammate and Following for
 * another); `defaultBucket` below is only the usual case.
 */
export const INBOX_BUCKETS = ['assigned', 'mentioned', 'following'] as const

export type InboxBucket = (typeof INBOX_BUCKETS)[number]

/** Higher wins when one event reaches the same person twice: Mentioned > Assigned > Following. */
export const INBOX_BUCKET_RANK: Record<InboxBucket, number> = { mentioned: 3, assigned: 2, following: 1 }

/** The bucket a person keeps when one event lists them more than once. */
export function strongestInboxBucket(buckets: readonly InboxBucket[]): InboxBucket | undefined {
  let best: InboxBucket | undefined
  for (const bucket of buckets) {
    if (best === undefined || INBOX_BUCKET_RANK[bucket] > INBOX_BUCKET_RANK[best]) {
      best = bucket
    }
  }
  return best
}

/** The fixed right-side layout a type is drawn in on the desktop notifications page. */
export const INBOX_FRAMEWORKS = [
  'decision',
  'conversation',
  'record',
  'checklist',
  'list',
  'problem',
  'announcement',
  'timeline',
  'generic',
  'bare',
] as const

export type InboxFramework = (typeof INBOX_FRAMEWORKS)[number]

/** Channels the inbox can deliver on. There is no SMS here. */
export type InboxChannel = 'IN_APP' | 'EMAIL' | 'PUSH'

export interface InboxNotificationTypeDefinition {
  /** Stable key stored on every notification, e.g. `tasks.task_assigned`. */
  key: string
  app: InboxApp
  /** The app's own type string today (Tasker `notificationType`, RFP `type`, Factory `kind`, Customers category). */
  sourceType: string
  /** Settings row name. */
  label: string
  /** What causes it, in one line, for the settings page. */
  description: string
  defaultBucket: InboxBucket
  framework: InboxFramework
  /** Channels this type can ever use. IN_APP is always present: the bell can't be switched off. */
  channels: readonly InboxChannel[]
  /**
   * A summary a person asks for, not an event that happened to them. The bell
   * switch exists for this type only, and it starts OFF: nobody gets one until
   * they turn it on in HQ. Every other type's bell stays always on.
   */
  optIn: boolean
  /** Shipped default for a new account. Push is on for Assigned and Mentioned only. */
  defaults: Record<InboxChannel, boolean>
  /** Boombox template used for email, or null when the type has no email. */
  emailTemplate: string | null
  /** One-click answers shown in the notification. */
  actions: readonly ('approve' | 'decline')[]
  status: NotificationRuleStatus
}

interface InboxTypeInput {
  key: string
  app: InboxApp
  sourceType: string
  label: string
  description: string
  bucket: InboxBucket
  framework: InboxFramework
  /** Today's email default for this type, or null when the app sends no email for it. */
  email: boolean | null
  emailTemplate?: string
  actions?: readonly ('approve' | 'decline')[]
  optIn?: true
  /** LIVE once the app sends it; every other app's rows are NOT_BUILT until its switch-over flips them. */
  status?: 'LIVE'
}

const INBOX_EMAIL_TEMPLATES: Record<InboxApp, string | null> = {
  tasks: 'tasks-notification',
  rfps: 'rfps-notification',
  customers: 'customers-notification-1',
  factory: null,
  schedule: null,
  hq: null,
}

function inboxType(input: InboxTypeInput): InboxNotificationTypeDefinition {
  const hasEmail = input.email !== null && INBOX_EMAIL_TEMPLATES[input.app] !== null
  const pushOn = input.bucket === 'assigned' || input.bucket === 'mentioned'
  return {
    key: input.key,
    app: input.app,
    sourceType: input.sourceType,
    label: input.label,
    description: input.description,
    defaultBucket: input.bucket,
    framework: input.framework,
    channels: hasEmail ? ['IN_APP', 'EMAIL', 'PUSH'] : ['IN_APP', 'PUSH'],
    optIn: input.optIn === true,
    defaults: { IN_APP: input.optIn !== true, EMAIL: hasEmail && input.email === true, PUSH: pushOn },
    emailTemplate: hasEmail ? (input.emailTemplate ?? INBOX_EMAIL_TEMPLATES[input.app]) : null,
    actions: input.actions ?? [],
    status: input.status ?? 'NOT_BUILT',
  }
}

export const INBOX_NOTIFICATION_TYPES: readonly InboxNotificationTypeDefinition[] = [
  // Tasks. Email defaults match today: every type emails except the two
  // handover notices, which Tasker sends as system notifications (push only).
  inboxType({
    key: 'tasks.task_assigned',
    app: 'tasks',
    sourceType: 'task_assigned',
    label: 'Task assigned',
    description: 'Someone assigns you a task',
    bucket: 'assigned',
    framework: 'record',
    email: true,
  }),
  inboxType({
    key: 'tasks.mention',
    app: 'tasks',
    sourceType: 'mention',
    label: 'Mentioned on a task',
    description: 'Someone @mentions you in a task comment',
    bucket: 'mentioned',
    framework: 'conversation',
    email: true,
  }),
  inboxType({
    key: 'tasks.comment_added',
    app: 'tasks',
    sourceType: 'comment_added',
    label: 'Comment on a task you follow',
    description: 'Someone comments on a task you created, are assigned or follow',
    bucket: 'following',
    framework: 'conversation',
    email: true,
  }),
  inboxType({
    key: 'tasks.status_changed',
    app: 'tasks',
    sourceType: 'status_changed',
    label: 'Task status changed',
    description: 'A task you follow moves to a new status',
    bucket: 'following',
    framework: 'record',
    email: true,
  }),
  inboxType({
    key: 'tasks.task_completed',
    app: 'tasks',
    sourceType: 'task_completed',
    label: 'Task completed or approved',
    description: 'A task you follow is completed, or your task is approved',
    bucket: 'following',
    framework: 'generic',
    email: true,
  }),
  inboxType({
    key: 'tasks.due_approaching',
    app: 'tasks',
    sourceType: 'due_approaching',
    label: 'Task due soon',
    description: 'A task assigned to you is due within a day',
    bucket: 'assigned',
    framework: 'checklist',
    email: true,
  }),
  inboxType({
    key: 'tasks.task_overdue',
    app: 'tasks',
    sourceType: 'task_overdue',
    label: 'Task overdue',
    description: 'A task assigned to you is past its due date',
    bucket: 'assigned',
    framework: 'checklist',
    email: true,
  }),
  // Opt-in (Hunter, 2026-10-17: "The optional 'one morning digest' can be
  // turned on and off from the notification settings window"). It sits BESIDE
  // the per-task due-soon and overdue rows, which stay on for everyone.
  inboxType({
    key: 'tasks.due_digest',
    app: 'tasks',
    sourceType: 'due_digest',
    label: 'Morning due-date digest',
    description: 'One note each morning: your tasks due soon and newly overdue',
    bucket: 'assigned',
    framework: 'list',
    email: false,
    optIn: true,
  }),
  inboxType({
    key: 'tasks.tasks_reassigned',
    app: 'tasks',
    sourceType: 'tasks_reassigned',
    label: 'Tasks handed to you',
    description: 'A teammate’s open tasks are handed over to you',
    bucket: 'assigned',
    framework: 'list',
    email: false,
  }),
  inboxType({
    key: 'tasks.tasks_reassign_failed',
    app: 'tasks',
    sourceType: 'tasks_reassign_failed',
    label: 'Task handover failed',
    description: 'A handover of open tasks did not finish',
    bucket: 'assigned',
    framework: 'problem',
    email: false,
  }),

  // RFPs. ASSUMED defaults (Hunter, 2026-10-16: "people get emails when they're
  // assigned new tasks, mentions"): email on for Assigned and Mentioned, off for
  // Following. Today RFP email is effectively off because of a defaults bug.
  inboxType({
    key: 'rfps.deadline_reminder',
    app: 'rfps',
    sourceType: 'deadline_reminder',
    label: 'Proposal deadline coming up',
    description: 'A deal you own has a deadline coming up',
    bucket: 'assigned',
    framework: 'checklist',
    email: true,
    emailTemplate: 'rfps-deadline-reminder',
  }),
  inboxType({
    key: 'rfps.task_assigned',
    app: 'rfps',
    sourceType: 'task_assigned',
    label: 'RFP task assigned',
    description: 'Someone assigns you a task on a deal',
    bucket: 'assigned',
    framework: 'record',
    email: true,
    emailTemplate: 'rfps-task-assignment',
  }),
  inboxType({
    key: 'rfps.task_completed',
    app: 'rfps',
    sourceType: 'task_completed',
    label: 'RFP task completed',
    description: 'A task on a deal you’re on is completed',
    bucket: 'following',
    framework: 'generic',
    email: false,
  }),
  inboxType({
    key: 'rfps.comment_mention',
    app: 'rfps',
    sourceType: 'comment_mention',
    label: 'Mentioned in a note',
    description: 'Someone @mentions you in a deal note or comment',
    bucket: 'mentioned',
    framework: 'conversation',
    email: true,
  }),
  inboxType({
    key: 'rfps.comment_reply',
    app: 'rfps',
    sourceType: 'comment_reply',
    label: 'Reply to your note',
    description: 'Someone replies to your note or comment',
    bucket: 'mentioned',
    framework: 'conversation',
    email: true,
  }),
  inboxType({
    key: 'rfps.deal_update',
    app: 'rfps',
    sourceType: 'deal_update',
    label: 'Deal updated',
    description: 'A deal you’re on changes: approvals, compiles, extracted fields',
    bucket: 'following',
    framework: 'generic',
    email: false,
  }),
  inboxType({
    key: 'rfps.deal_assigned',
    app: 'rfps',
    sourceType: 'deal_assigned',
    label: 'Added to a deal',
    description: 'Someone adds you to a deal',
    bucket: 'assigned',
    framework: 'generic',
    email: true,
  }),
  inboxType({
    key: 'rfps.outcome_won',
    app: 'rfps',
    sourceType: 'outcome_won',
    label: 'Deal won',
    description: 'A deal you own or follow is marked won',
    bucket: 'following',
    framework: 'record',
    email: false,
  }),
  inboxType({
    key: 'rfps.outcome_lost',
    app: 'rfps',
    sourceType: 'outcome_lost',
    label: 'Deal lost',
    description: 'A deal you own or follow is marked lost',
    bucket: 'following',
    framework: 'record',
    email: false,
  }),
  inboxType({
    key: 'rfps.bond_assigned',
    app: 'rfps',
    sourceType: 'bond_assigned',
    label: 'Bond assigned',
    description: 'Someone assigns you a bond on a deal',
    bucket: 'assigned',
    framework: 'record',
    email: true,
  }),
  inboxType({
    key: 'rfps.bond_approval_requested',
    app: 'rfps',
    sourceType: 'bond_approval_requested',
    label: 'Bond approval requested',
    description: 'A bond needs your approval',
    bucket: 'assigned',
    framework: 'decision',
    email: true,
    actions: ['approve', 'decline'],
  }),
  inboxType({
    key: 'rfps.bond_approved',
    app: 'rfps',
    sourceType: 'bond_approved',
    label: 'Bond approved',
    description: 'A bond you’re on is approved',
    bucket: 'following',
    framework: 'record',
    email: false,
  }),
  inboxType({
    key: 'rfps.bond_secured',
    app: 'rfps',
    sourceType: 'bond_secured',
    label: 'Bond secured',
    description: 'A bond you’re on is secured',
    bucket: 'following',
    framework: 'record',
    email: false,
  }),
  inboxType({
    key: 'rfps.bond_status_change',
    app: 'rfps',
    sourceType: 'bond_status_change',
    label: 'Bond status changed',
    description: 'A bond you’re on changes status',
    bucket: 'following',
    framework: 'record',
    email: false,
  }),
  inboxType({
    key: 'rfps.signature_requested',
    app: 'rfps',
    sourceType: 'signature_requested',
    label: 'Signature requested',
    description: 'A document needs your signature',
    bucket: 'assigned',
    framework: 'record',
    email: true,
  }),
  inboxType({
    key: 'rfps.signature_completed',
    app: 'rfps',
    sourceType: 'signature_completed',
    label: 'Document signed',
    description: 'Someone signs a document you requested',
    bucket: 'following',
    framework: 'checklist',
    email: false,
  }),
  inboxType({
    key: 'rfps.signature_rejected',
    app: 'rfps',
    sourceType: 'signature_rejected',
    label: 'Signature rejected',
    description: 'Someone rejects a document you requested',
    bucket: 'following',
    framework: 'checklist',
    email: false,
  }),
  inboxType({
    key: 'rfps.signature_request_completed',
    app: 'rfps',
    sourceType: 'signature_request_completed',
    label: 'All signatures in',
    description: 'Every signature on a request is collected',
    bucket: 'following',
    framework: 'checklist',
    email: false,
  }),
  inboxType({
    key: 'rfps.shipment_assigned',
    app: 'rfps',
    sourceType: 'shipment_assigned',
    label: 'Shipment assigned',
    description: 'Someone assigns you a shipment on a deal',
    bucket: 'assigned',
    framework: 'record',
    email: true,
  }),
  inboxType({
    key: 'rfps.shipment_shipped',
    app: 'rfps',
    sourceType: 'shipment_shipped',
    label: 'Shipment sent',
    description: 'A shipment on a deal you’re on is sent',
    bucket: 'following',
    framework: 'record',
    email: false,
  }),
  inboxType({
    key: 'rfps.shipment_delivered',
    app: 'rfps',
    sourceType: 'shipment_delivered',
    label: 'Shipment delivered',
    description: 'A shipment on a deal you’re on is delivered',
    bucket: 'following',
    framework: 'record',
    email: false,
  }),
  inboxType({
    key: 'rfps.website_change',
    app: 'rfps',
    sourceType: 'website_change',
    label: 'RFP portal changed',
    description: 'A monitored RFP portal posts a change, like an addendum',
    bucket: 'following',
    framework: 'list',
    email: false,
  }),
  inboxType({
    key: 'rfps.reputation_alert',
    app: 'rfps',
    sourceType: 'reputation_alert',
    label: 'Reputation warning',
    description: 'A reputation check flags a problem on a deal you’re on',
    bucket: 'following',
    framework: 'problem',
    email: false,
  }),
  inboxType({
    key: 'rfps.system',
    app: 'rfps',
    sourceType: 'system',
    label: 'Joined your organization',
    description: 'Someone accepts an invitation to your RFPs organization',
    bucket: 'following',
    framework: 'generic',
    email: false,
  }),

  // Customers (staff categories only; family notifications stay on the customer rail).
  inboxType({
    key: 'customers.note_mention',
    app: 'customers',
    sourceType: 'CUSTOMER_NOTE_MENTION',
    label: 'Mentioned in a customer note',
    description: 'A teammate @mentions you on a customer record note',
    bucket: 'mentioned',
    framework: 'conversation',
    email: true,
  }),
  inboxType({
    key: 'customers.note_reply',
    app: 'customers',
    sourceType: 'CUSTOMER_NOTE_REPLY',
    label: 'Reply to a customer note',
    description: 'Someone replies to a customer record note you’re on',
    bucket: 'mentioned',
    framework: 'conversation',
    email: true,
  }),
  inboxType({
    key: 'customers.task_assigned',
    app: 'customers',
    sourceType: 'CUSTOMER_TASK_ASSIGNED',
    label: 'Customer task assigned',
    description: 'Someone assigns you a customer task',
    bucket: 'assigned',
    framework: 'record',
    email: true,
  }),
  inboxType({
    key: 'customers.work_order_assigned',
    app: 'customers',
    sourceType: 'CUSTOMER_WORK_ORDER_ASSIGNED',
    label: 'Work order assigned',
    description: 'Someone assigns you a work order',
    bucket: 'assigned',
    framework: 'record',
    email: true,
  }),
  inboxType({
    key: 'customers.school_directory_import',
    app: 'customers',
    sourceType: 'SYSTEM_ANNOUNCEMENT',
    label: 'School directory import finished',
    description: 'A school directory import you started finishes or fails',
    bucket: 'following',
    framework: 'record',
    email: false,
  }),

  // Factory. No email template exists yet, so these are bell and push only.
  inboxType({
    key: 'factory.gate_failed',
    app: 'factory',
    sourceType: 'gate.failed',
    label: 'Checks failed',
    description: 'Checks fail on an agent session you started',
    bucket: 'assigned',
    framework: 'problem',
    email: null,
  }),
  inboxType({
    key: 'schedule.assignment_proposed',
    app: 'schedule',
    sourceType: 'assignment_proposed',
    label: 'Assignment to confirm',
    description: 'Someone puts you on a Schedule event and needs you to confirm',
    bucket: 'assigned',
    framework: 'record',
    email: null,
  }),
  // Schedule owns its explicit email/SMS delivery through Boombox. These add
  // receiver support first without enabling duplicate notification-service email.
  inboxType({
    key: 'schedule.chat_message',
    app: 'schedule',
    sourceType: 'chat_message',
    label: 'New message',
    description: 'A teammate or ambassador sends a message in your Schedule conversation',
    bucket: 'assigned',
    framework: 'conversation',
    email: false,
    status: 'LIVE',
  }),
  inboxType({
    key: 'schedule.offer_received',
    app: 'schedule',
    sourceType: 'offer_sent',
    label: 'Ambassador opportunity offered',
    description: 'You receive an ambassador opportunity to accept or pass on',
    bucket: 'assigned',
    framework: 'record',
    email: false,
    status: 'LIVE',
  }),
  inboxType({
    key: 'schedule.offer_accepted',
    app: 'schedule',
    sourceType: 'offer_accepted',
    label: 'Ambassador offer accepted',
    description: 'An ambassador accepts an offer for an event you own',
    bucket: 'assigned',
    framework: 'record',
    email: false,
    status: 'LIVE',
  }),
  inboxType({
    key: 'schedule.booking_confirmed',
    app: 'schedule',
    sourceType: 'booking_confirmed',
    label: 'Ambassador booking confirmed',
    description: 'Your accepted ambassador booking is confirmed with event details',
    bucket: 'assigned',
    framework: 'record',
    email: false,
    status: 'LIVE',
  }),
  inboxType({
    key: 'schedule.reminder_day_before',
    app: 'schedule',
    sourceType: 'reminder_day_before',
    label: 'Event reminder for tomorrow',
    description: 'Your booked ambassador event is tomorrow',
    bucket: 'assigned',
    framework: 'record',
    email: false,
    status: 'LIVE',
  }),
  inboxType({
    key: 'schedule.reminder_day_of',
    app: 'schedule',
    sourceType: 'reminder_day_of',
    label: 'Event reminder for today',
    description: 'Your booked ambassador event is today',
    bucket: 'assigned',
    framework: 'record',
    email: false,
    status: 'LIVE',
  }),
  inboxType({
    key: 'schedule.offer_declined',
    app: 'schedule',
    sourceType: 'offer_declined',
    label: 'Ambassador offer declined',
    description: 'An ambassador passes on an offer for an event you own',
    bucket: 'assigned',
    framework: 'record',
    email: false,
    status: 'LIVE',
  }),
  inboxType({
    key: 'schedule.offer_withdrawn',
    app: 'schedule',
    sourceType: 'offer_withdrawn',
    label: 'Ambassador offer withdrawn',
    description: 'Ordo withdraws an ambassador opportunity offered to you',
    bucket: 'assigned',
    framework: 'record',
    email: false,
    status: 'LIVE',
  }),
  inboxType({
    key: 'schedule.offer_expired',
    app: 'schedule',
    sourceType: 'offer_expired',
    label: 'Ambassador offer expired',
    description: 'An ambassador offer reaches its answer-by time without acceptance',
    bucket: 'assigned',
    framework: 'problem',
    email: false,
    status: 'LIVE',
  }),
  inboxType({
    key: 'schedule.report_submitted',
    app: 'schedule',
    sourceType: 'report_submitted',
    label: 'Ambassador report ready for review',
    description: 'An ambassador submits a report for an event you own',
    bucket: 'assigned',
    framework: 'record',
    email: false,
    status: 'LIVE',
  }),
  inboxType({
    key: 'schedule.report_needs_fix',
    app: 'schedule',
    sourceType: 'report_changes_requested',
    label: 'Ambassador report needs changes',
    description: 'Ordo requests changes to your submitted ambassador report',
    bucket: 'assigned',
    framework: 'problem',
    email: false,
    status: 'LIVE',
  }),
  inboxType({
    key: 'schedule.report_approved',
    app: 'schedule',
    sourceType: 'report_approved',
    label: 'Ambassador report approved',
    description: 'Ordo approves your submitted ambassador report',
    bucket: 'assigned',
    framework: 'record',
    email: false,
    status: 'LIVE',
  }),
  inboxType({
    key: 'schedule.job_changed',
    app: 'schedule',
    sourceType: 'event_updated',
    label: 'Ambassador event updated',
    description: 'Details change for an ambassador event you are assigned to',
    bucket: 'assigned',
    framework: 'record',
    email: false,
    status: 'LIVE',
  }),
  inboxType({
    key: 'schedule.job_canceled',
    app: 'schedule',
    sourceType: 'event_canceled',
    label: 'Ambassador event canceled',
    description: 'An ambassador event you are assigned to is canceled',
    bucket: 'assigned',
    framework: 'problem',
    email: false,
    status: 'LIVE',
  }),
  inboxType({
    key: 'schedule.job_reopened',
    app: 'schedule',
    sourceType: 'event_reopened',
    label: 'Ambassador event reopened',
    description: 'An ambassador event you are assigned to is reopened',
    bucket: 'assigned',
    framework: 'record',
    email: false,
    status: 'LIVE',
  }),
  inboxType({
    key: 'factory.pr_opened',
    app: 'factory',
    sourceType: 'pr.opened',
    label: 'Pull request ready for review',
    description: 'An agent you started opens a pull request',
    bucket: 'assigned',
    framework: 'record',
    email: null,
  }),
  inboxType({
    key: 'factory.pr_merged',
    app: 'factory',
    sourceType: 'pr.merged',
    label: 'Pull request merged',
    description: 'A pull request from an agent you started is merged',
    bucket: 'following',
    framework: 'generic',
    email: null,
  }),
  inboxType({
    key: 'factory.pr_closed',
    app: 'factory',
    sourceType: 'pr.closed',
    label: 'Pull request closed',
    description: 'A pull request from an agent you started is closed without merging',
    bucket: 'following',
    framework: 'generic',
    email: null,
  }),
  // HQ's weekly and school-year awards (PROD-1556): winners only, bell and push,
  // no email. Sent at Monday 9 a.m. for the week (or year) that just ended,
  // when Settings → Awards → Notify winners is on (`managers/awards.ts`).
  inboxType({
    key: 'hq.award_won_week',
    app: 'hq',
    sourceType: 'award_won_week',
    label: 'Weekly award won',
    description: 'You won one of the weekly awards last week',
    bucket: 'mentioned',
    framework: 'announcement',
    email: null,
    status: 'LIVE',
  }),
  inboxType({
    key: 'hq.award_won_year',
    app: 'hq',
    sourceType: 'award_won_year',
    label: 'School-year award won',
    description: 'You won one of the school-year awards (the most weekly wins)',
    bucket: 'mentioned',
    framework: 'announcement',
    email: null,
    status: 'LIVE',
  }),
]

/** Inbox types indexed by key. */
export const INBOX_NOTIFICATION_TYPES_BY_KEY: Readonly<Record<string, InboxNotificationTypeDefinition>> = Object.freeze(
  Object.fromEntries(INBOX_NOTIFICATION_TYPES.map((type) => [type.key, type])),
)

/** True when `key` names an inbox type. Unknown keys are refused by the service. */
export function isInboxNotificationTypeKey(key: string): boolean {
  return Object.hasOwn(INBOX_NOTIFICATION_TYPES_BY_KEY, key)
}

/** The inbox type an app's own type string maps to, if it is carried in the inbox. */
export function inboxTypeForSource(app: InboxApp, sourceType: string): InboxNotificationTypeDefinition | undefined {
  return INBOX_NOTIFICATION_TYPES.find((type) => type.app === app && type.sourceType === sourceType)
}

/** Inbox types for one app, in registry order. Drives the HQ settings page grouping. */
export function inboxTypesForApp(app: InboxApp): InboxNotificationTypeDefinition[] {
  return INBOX_NOTIFICATION_TYPES.filter((type) => type.app === app)
}
